import { Form, SchemaFormHook, useForm, useFormContext } from "@/components/form";
import { useStore } from "@tanstack/react-form";
import { useEffect } from "react";
import { Articulo } from "@/shared/types/articulo";
import { CircularProgress, Collapse, Stack, StackProps, Switch, Typography } from "@mui/material";
import { z } from "zod/v4";
import SearchIcon from '@mui/icons-material/Search';
import EditNoteIcon from '@mui/icons-material/EditNote';
import useAfterCall from "@/hooks/use-after-call";
import useFetch from "@/hooks/use-fetch";
import { searchArticulo } from "@/features/inventario/articulos/api/search-articulo";
import { notifyError } from "@/shared/notification";
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import VentaArticuloManualSearch from "./venta-articulo-manual-search";
import { GetVentaAuthorizationMutation } from "../../api/get-venta-authorization";
import VentaAuthorizationCodeForm, { ventaAuthorizationCodeFormSchema } from "./venta-authorization-code-form";
import { DataCache } from "@/components/display";
import PreviewArticulo from "@/features/inventario/articulos/components/preview-articulo";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";

interface VentaArticuloSearchProps extends StackProps {
    onArticuloSelect?: (value: {
        articulo: Articulo,
        cantidad: number,
        precio: number,
        precioIndex: number,
    }) => void
}


const ventaArticuloSearchFormSchema = z.object({
    articulo: z.looseObject({}, { message: "Debes seleccionar un artículo" }),
    codigo: z.string(),
    cantidad: z.number({ message: "Este campo es requerido" }).min(1, "El valor debe ser mayor a 0"),
    precioIndex: z.number({ message: "Este campo es requerido" }),
    precio: z.number({ message: "Este campo es requerido" }).min(1, "El valor debe ser mayor a 0"),
})

export default function VentaArticuloSearch({
    onArticuloSelect,
    ...props
}: VentaArticuloSearchProps) {
    const openModal = useOpenModal();
    const parentForm = useFormContext();
    const { requireVentaAuthorization, defaultPrecioIndex } = useConfigurationContext();
    const parentToken = useStore(parentForm.store, state => (state.values as any).token as string | undefined);
    const unlocked = !requireVentaAuthorization || !!parentToken;

    const form = useForm({
        defaultValues: {
            articulo: undefined as unknown,
            codigo: "",
            cantidad: 1,
            precio: "" as unknown,
            precioIndex: unlocked ? (defaultPrecioIndex ?? 0) : Math.min(defaultPrecioIndex ?? 0, 1)
        } as z.infer<typeof ventaArticuloSearchFormSchema>,
        submitOnce: false,
        closeModalOnSubmit: false,
        validators: {
            onSubmit: ventaArticuloSearchFormSchema
        },
        enterBehaviour: "selectNext",
        onSubmit: ({ value, formApi }) => {
            const articulo = value.articulo! as Articulo;
            onArticuloSelect && onArticuloSelect({
                articulo: articulo,
                cantidad: value.cantidad,
                precio: value.precioIndex >= 0 ? articulo.precios[value.precioIndex] : value.precio,
                precioIndex: value.precioIndex
            });
            formApi.reset();
            formApi.setFieldValue("precioIndex", value.precioIndex)
            focusField("codigo");
        },
        parentForm: parentForm
    })

    useEffect(() => {
        if (!unlocked) {
            const current = form.getFieldValue("precioIndex");
            if (current > 1) form.setFieldValue("precioIndex", Math.min(defaultPrecioIndex ?? 0, 1));
        }
    }, [unlocked]);

    const focusField = useAfterCall((name: string) => form.focusField(name, true));

    const { loading, error, fetch } = useFetch(async (codigo: string) => {
        if (!codigo) return undefined;

        const isAuto = parentForm.getFieldValue("auto");

        const result = await searchArticulo(codigo, isAuto != null ? !isAuto : true);
        return result || null;
    }, {
        onError: (e) => notifyError(e),
        onSuccess: (v) => {
            form.setFieldValue("articulo", v!);
            if (!v) {
                focusField("codigo");
                return;
            }
            const auto = parentForm.getFieldValue("auto");
            form.setFieldValue("codigo", v.codigo);
            if (!form.getFieldValue("cantidad")) form.setFieldValue("cantidad", 1);
            if (!form.getFieldValue("precio")) form.setFieldValue("precio", v.precios[0]);

            if (!auto) focusField("cantidad");
            else form.handleSubmit();
        }
    })

    return <Form form={form} gap={2} {...props}>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography variant="h6">Agregar Artículos</Typography>
            <Stack gap={2} direction={"row"} alignItems={"center"} >
                <parentForm.Field name="auto">
                    {field => <Stack direction={"row"} alignItems={"center"}>
                        <Typography variant="body2" color={(form.disabled) ? "text.disabled" : "text.primary"}>Auto</Typography>
                        <Switch disabled={form.disabled} checked={field.state.value} onChange={(_, v) => field.handleChange(v)} />
                    </Stack>}
                </parentForm.Field>
                {<Form.IconButton size="small" onClick={() => openModal("searchArticulo")}>
                    <SearchIcon />
                </Form.IconButton>}
                {requireVentaAuthorization && <parentForm.Subscribe selector={state => state.values.token}>
                    {token => <Form.IconButton size="small" onClick={() => !token && openModal("editorMode")}>
                        <EditNoteIcon color={token ? "primary" : undefined} />
                    </Form.IconButton>}
                </parentForm.Subscribe>}
            </Stack>
        </Stack>

        <Form.TextField
            name="codigo" label="Código" size="small" disabled={loading}
            onKeyDown={e => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    fetch(form.getFieldValue("codigo"));
                }
            }}
            slotProps={{
                input: {
                    endAdornment: <form.Subscribe selector={state => state.values.codigo}>
                        {codigo => loading && codigo ? <CircularProgress size={20} /> : undefined}
                    </form.Subscribe>
                }
            }}
        />
        <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Stack flex={"1 0 150px"}>
                <Form.NumberField name="cantidad" label="Cantidad" size="small" decimalScale={0} alignRight />
            </Stack>
            <Stack flex={"5 0 150px"}>
                <Form.Select name="precioIndex" label="Precio" size="small">
                    <Form.Select.Option value={0}>Precio 1</Form.Select.Option>
                    <Form.Select.Option value={1}>Precio 2</Form.Select.Option>
                    {unlocked && <Form.Select.Option value={2}>Precio 3</Form.Select.Option>}
                    {unlocked && <Form.Select.Option value={-1}><i>Editar</i></Form.Select.Option>}
                </Form.Select>
            </Stack>
        </Stack>
        <form.Subscribe selector={state => state.values.precioIndex}>
            {precioIndex => precioIndex < 0 && <Form.MonedaField name="precio" label="Editar Precio" size="small" />}
        </form.Subscribe>
        <form.Subscribe selector={state => state.values.articulo}>
            {articulo => <parentForm.Subscribe selector={state => state.values.auto}>
                {auto => !auto && <>
                    <Form.SubmitButton disabled={!articulo}>
                        Agregar
                        <Form.ErrorText name="articulo" />
                    </Form.SubmitButton>
                    <Collapse in={!loading && articulo !== undefined}>
                        <DataCache data={articulo}>
                            {articulo => <PreviewArticulo articulo={articulo as Articulo} error={error} />}
                        </DataCache>
                    </Collapse>
                </>}
            </parentForm.Subscribe>}
        </form.Subscribe>

        <Dialog code="searchArticulo" size="lg">
            <ModalHeader>Buscar Artículo</ModalHeader>
            <ModalContextExtractor>
                {({ handleClose }) => <VentaArticuloManualSearch
                    padding={3}
                    onArticuloSelect={articulo => {
                        form.setFieldValue("codigo", articulo.codigo)
                        fetch(articulo.codigo);
                        handleClose();
                    }}
                />}
            </ModalContextExtractor>
        </Dialog>

        <Dialog code="editorMode" size="sm">
            <ModalHeader>Ingresar Código de Autorización</ModalHeader>
            <GetVentaAuthorizationMutation>
                {mutation => <SchemaFormHook
                    schema={ventaAuthorizationCodeFormSchema}
                    onSubmit={async ({ value }) => {
                        const token = await mutation.mutateAsync(value)
                        parentForm.setFieldValue("token", token);
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <VentaAuthorizationCodeForm form={form} padding={3} />}
                </SchemaFormHook>}
            </GetVentaAuthorizationMutation>
        </Dialog>
    </Form>
}